'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import OtpModal from './OtpModal';
import { useAuth } from '@/contexts/AuthContext';

export default function BuyerRegisterForm() {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const router = useRouter();
  const { signInWithPhone } = useAuth();

  const validate = () => {
    let valid = true;
    if (!name.trim()) {
      setNameError('Name is required');
      valid = false;
    } else {
      setNameError('');
    }
    if (!phone) {
      setPhoneError('Mobile number is required');
      valid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      setPhoneError('Enter a valid 10-digit mobile number');
      valid = false;
    } else {
      setPhoneError('');
    }
    return valid;
  };

  const handleSendOtp = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      await signInWithPhone(phone);
      toast.success(`OTP sent to +91 ${phone}`);
      setOtpOpen(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = async () => {
    setOtpOpen(false);
    toast.success('Account created! Welcome to LocalMandi.');
    await new Promise((r) => setTimeout(r, 400));
    router.push('/marketplace');
  };

  return (
    <>
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="buyer-name" className="block text-sm font-600 text-foreground mb-1.5">
            Your Name <span className="text-negative">*</span>
          </label>
          <input
            id="buyer-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            className={`input-field ${nameError ? 'input-error' : ''}`}
            placeholder="Rajesh Kumar"
            autoComplete="name"
          />
          {nameError && <p className="text-xs text-negative mt-1">{nameError}</p>}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="reg-phone" className="block text-sm font-600 text-foreground mb-1.5">
            Mobile Number <span className="text-negative">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-lg border border-border bg-muted text-sm text-muted-foreground font-600 select-none">
              +91
            </div>
            <input
              id="reg-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                setPhone(val);
                if (phoneError) setPhoneError('');
              }}
              className={`input-field flex-1 ${phoneError ? 'input-error' : ''}`}
              placeholder="98765 43210"
              inputMode="numeric"
              autoComplete="tel"
            />
          </div>
          {phoneError && <p className="text-xs text-negative mt-1">{phoneError}</p>}
          <p className="text-xs text-muted-foreground mt-1.5">
            A 6-digit OTP will be sent to verify your mobile number
          </p>
        </div>

        {/* Send OTP Button */}
        <button
          type="button"
          disabled={isLoading}
          onClick={handleSendOtp}
          className="w-full py-2.5 rounded-lg text-sm font-700 btn-primary flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              <span>Sending OTP...</span>
            </>
          ) : (
            <>
              <Icon name="DevicePhoneMobileIcon" size={16} />
              <span>Create Account &amp; Send OTP</span>
            </>
          )}
        </button>
      </div>

      <OtpModal
        open={otpOpen}
        phone={phone}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        isBuyerRegister
        buyerName={name}
      />
    </>
  );
}